<template>
  <section
    ref="promoBarRef"
    v-style:m="{ sm: mobileBlendModeActive ? '0.75rem 0 0 5.5rem' : '0', lg: '0' }"
    :aria-label="$t.promoBar"
    class="relative z-header h-8 overflow-hidden p-2 text-xs"
    :class="{
      'bg-black c-white': header.transparent,
      '<lg:fixed <lg:bg-transparent <lg:mix-blend-difference <lg:z-1 <lg:c-white <lg:[mask-image:var(--mi)]': mobileBlendModeActive,
    }"
    :style="mobileBlendModeActive
      ? '--mi: linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
      : ''"
  >
    <client-only v-if="isTickerMode">
      <base-ticker
        ref="tickerRef"
        :active="activeIndex"
        :interval="content.autorotateTime"
        :max="displayedMessages.length"
        @change="activeIndex = $event"
      >
        <transition-group
          :class="{ 'lg:grid lg:gap-4': content.mode === 'multiple_static' }"
          name="fade"
          :style="content.mode === 'multiple_static'
            ? { gridTemplateColumns: `repeat(${currentGroupSize}, 1fr)` }
            : undefined"
          :tag="content.mode === 'multiple_static' ? 'div' : undefined"
        >
          <component
            :is="content.directLinks ? BaseLink : BaseButton"
            v-show="isMessageVisible(i)"
            v-for="(message, i) in displayedMessages as Message[]"
            :key="message.id"
            class="w-full text-center duration-500"
            :class="content.mode === 'multiple_static' ? '<lg:absolute <lg:inset-y-0' : 'absolute inset-y-0'"
            :target="content.directLinks ? message.targets?.[0]?.targetAttribute : undefined"
            :to="content.directLinks ? message.targets?.[0]?.url : undefined"
            @click="handleClick"
          >
            <cms-section :section="{ items: [message], name: 'promo-bar-message' }" />
          </component>
        </transition-group>
      </base-ticker>
    </client-only>
    <base-marquee v-else>
      <template #default="{ cloned }">
        <div
          v-for="(message, i) in displayedMessages as Message[]"
          :key="i"
          ref="contentRef"
          class="flex items-center"
        >
          <component
            :is="content.directLinks ? BaseLink : BaseButton"
            :tabindex="cloned ? '-1' : undefined"
            :target="content.directLinks ? message.targets?.[0]?.targetAttribute : undefined"
            :to="content.directLinks ? message.targets?.[0]?.url : undefined"
            @click="handleClick"
          >
            <cms-section
              :key="i"
              :section="{ items: [message], name: 'promo-bar-message' }"
            />
          </component>
          <span aria-hidden="true" class="mx-6 shrink-0">•</span>
        </div>
      </template>
    </base-marquee>
    <panel-promo-bar v-if="!content.directLinks" :content />
  </section>
</template>

<script lang="ts" setup>
import type { Message, PromoBarContent } from '#types/components/cms/promo-bar'
import { BaseButton, BaseLink } from '#components'

const { content } = defineProps<{ content: PromoBarContent }>()
const { maxPromoMessages, allowMixBlendMode } = useAppConfig().components.cms.promoBar
const emptySections = useState<Record<string, boolean>>('emptySections', () => ({}))
const { $feature, $viewport } = useNuxtApp()
const header = useHeaderStore()
const route = useRoute()

const PanelPromoBar = createPanel('promo-bar')

const contentRef = ref()
const promoBarRef = toRef(header.refs, 'promoBar')

const hovered = useElementHover(promoBarRef)
const { focused } = useFocusWithin(promoBarRef)
const activeIndex = ref(0)
const tickerRef = useTemplateRef('tickerRef')

const handleClick = () => {
  if (!content.directLinks)
    PanelPromoBar.open()
}

const mobileBlendModeActive = computed(
  () => allowMixBlendMode && $feature.headerMobilePromoBlendModeRoutes.some(
    (pattern) => convertPatternToRegex(pattern).test(route.path)
  )
)

const displayedMessages = computed(
  () => content.messages.filter((message) => !emptySections.value[message.id]).slice(0, maxPromoMessages)
)

const isTickerMode = computed(() => content.mode === 'static' || content.mode === 'multiple_static')

const GROUP_SIZE = 4
const groupIndexOf = (i: number) => Math.floor(i / GROUP_SIZE)
const isInActiveGroup = (i: number) => groupIndexOf(i) === groupIndexOf(activeIndex.value)
const currentGroupSize = computed(
  () => Math.min(GROUP_SIZE, displayedMessages.value.length - groupIndexOf(activeIndex.value) * GROUP_SIZE)
)

const isMessageVisible = (i: number) => content.mode === 'multiple_static'
  ? ($viewport.lg ? isInActiveGroup(i) : i === activeIndex.value)
  : i === activeIndex.value

if (isTickerMode.value) {
  watch([focused, hovered], ([focused, hovered]) => {
    if (focused || hovered)
      tickerRef.value?.pause()
    else
      tickerRef.value?.resume()
  })
}
</script>
